import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, TableSkeleton } from "@/components/common/Common";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShoppingCart, Plus, MoreHorizontal, Trash2, Download, Search, X, Eye } from "lucide-react";

const fmt = (n) => `TZS ${Number(n || 0).toLocaleString()}`;

export default function SalesPage() {
  const { t, can, user } = useApp();
  const [sales, setSales] = useState(null);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [detail, setDetail] = useState(null);

  // new sale state
  const [branchId, setBranchId] = useState("");
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [picker, setPicker] = useState("");
  const [customer, setCustomer] = useState("");
  const [payment, setPayment] = useState("cash");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, b] = await Promise.all([api.get("/sales"), api.get("/branches").catch(() => ({ data: [] }))]);
      setSales(s.data); setBranches(b.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    const b = branches[0]?.id || "";
    setBranchId(b); setCart([]); setCustomer(""); setPayment("cash"); setPicker("");
    setDialog(true);
  };

  useEffect(() => {
    if (dialog && branchId) api.get(`/products?branch_id=${branchId}`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, [dialog, branchId]);

  const addToCart = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    setCart((c) => c.find((i) => i.id === pid) ? c.map((i) => i.id === pid ? { ...i, qty: i.qty + 1 } : i) : [...c, { id: p.id, name: p.name, price: p.price, qty: 1, stock: p.stock_qty }]);
    setPicker("");
  };
  const setQty = (id, qty) => setCart((c) => c.map((i) => i.id === id ? { ...i, qty: Math.max(1, qty) } : i));
  const removeItem = (id) => setCart((c) => c.filter((i) => i.id !== id));
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/sales", { branch_id: branchId, items: cart.map((i) => ({ product_id: i.id, qty: i.qty })), payment_method: payment, customer_name: customer });
      toast.success(`Sale ${data.invoice_no} recorded — ${fmt(data.total)}`);
      setDialog(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doVoid = async (s) => {
    try { await api.delete(`/sales/${s.id}`); toast.success("Sale voided; stock restored"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setConfirm(null);
  };
  const exportCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sales/export`, "_blank");

  const filtered = (sales || []).filter((s) => !search || `${s.invoice_no} ${s.customer_name}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title={t("sales")} subtitle={t("salesOverview")}>
        {can("reports.export") && <Button variant="outline" size="sm" className="gap-2" onClick={exportCsv} data-testid="sales-export-btn"><Download className="h-4 w-4" />{t("export")}</Button>}
        {can("sales.create") && <Button size="sm" className="gap-2" onClick={openNew} data-testid="new-sale-btn"><Plus className="h-4 w-4" />{t("newSale")}</Button>}
      </PageHeader>

      <div className="bg-card border border-border rounded-md">
        <div className="p-4 border-b border-border">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="sales-search" />
          </div>
        </div>
        {sales === null ? <TableSkeleton cols={6} /> : filtered.length === 0 ? (
          <EmptyState icon={ShoppingCart} title={t("noSales")}
            action={can("sales.create") && <Button size="sm" className="gap-2" onClick={openNew}><Plus className="h-4 w-4" />{t("newSale")}</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("invoice")}</TableHead><TableHead>{t("customer")}</TableHead><TableHead>{t("items")}</TableHead>
                <TableHead>{t("total")}</TableHead><TableHead>{t("paymentMethod")}</TableHead><TableHead>{t("branch")}</TableHead>
                <TableHead>Date</TableHead><TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} data-testid={`sale-row-${s.invoice_no}`}>
                    <TableCell className="font-mono text-xs font-semibold">{s.invoice_no}</TableCell>
                    <TableCell>{s.customer_name}</TableCell>
                    <TableCell>{s.items?.length}</TableCell>
                    <TableCell className="font-semibold whitespace-nowrap">{fmt(s.total)}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] capitalize">{s.payment_method}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.branch_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`sale-actions-${s.invoice_no}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetail(s)} data-testid={`sale-view-${s.invoice_no}`}><Eye className="h-4 w-4 mr-2" />{t("view")}</DropdownMenuItem>
                          {can("sales.delete") && <DropdownMenuItem className="text-destructive" onClick={() => setConfirm(s)} data-testid={`sale-void-${s.invoice_no}`}><Trash2 className="h-4 w-4 mr-2" />{t("voidSale")}</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* New Sale */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("newSale")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("branch")} *</Label>
              <Select value={branchId} onValueChange={(v) => { setBranchId(v); setCart([]); }}>
                <SelectTrigger className="mt-1.5" data-testid="sale-branch-select"><SelectValue placeholder={t("selectBranch")} /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("customer")}</Label><Input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Walk-in" className="mt-1.5" data-testid="sale-customer-input" /></div>
          </div>
          <div>
            <Label>{t("addItem")}</Label>
            <Select value={picker} onValueChange={addToCart}>
              <SelectTrigger className="mt-1.5" data-testid="sale-product-select"><SelectValue placeholder="Select a product to add" /></SelectTrigger>
              <SelectContent>
                {products.filter((p) => p.stock_qty > 0).map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`sale-product-opt-${p.sku}`}>{p.name} — {fmt(p.price)} ({p.stock_qty} in stock)</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="border border-border rounded-md">
            {cart.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">{t("cart")} is empty</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>{t("name")}</TableHead><TableHead>{t("price")}</TableHead><TableHead className="w-24">{t("quantity")}</TableHead><TableHead>{t("total")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {cart.map((i) => (
                    <TableRow key={i.id} data-testid={`cart-row-${i.id}`}>
                      <TableCell className="font-medium">{i.name}</TableCell>
                      <TableCell>{fmt(i.price)}</TableCell>
                      <TableCell><Input type="number" min={1} max={i.stock} value={i.qty} onChange={(e) => setQty(i.id, Number(e.target.value))} className="h-8 w-20" data-testid={`cart-qty-${i.id}`} /></TableCell>
                      <TableCell className="font-semibold">{fmt(i.price * i.qty)}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => removeItem(i.id)} data-testid={`cart-remove-${i.id}`}><X className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="w-40">
              <Label>{t("paymentMethod")}</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger className="mt-1.5" data-testid="sale-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="mpesa">M-Pesa</SelectItem><SelectItem value="card">Card</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{t("total")}</p>
              <p className="text-2xl font-heading font-bold text-primary" data-testid="sale-total">{fmt(total)}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t("cancel")}</Button>
            <Button onClick={submit} disabled={saving || !branchId || cart.length === 0} data-testid="sale-complete-btn">{t("completeSale")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sale detail */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{detail?.invoice_no}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-muted-foreground"><span>{detail.customer_name}</span><span>{new Date(detail.created_at).toLocaleString()}</span></div>
              <div className="border-t border-border pt-2 space-y-1">
                {detail.items.map((it, idx) => (
                  <div key={idx} className="flex justify-between"><span>{it.name} × {it.qty}</span><span>{fmt(it.subtotal)}</span></div>
                ))}
              </div>
              <div className="border-t border-border pt-2 flex justify-between font-semibold text-base"><span>{t("total")}</span><span className="text-primary">{fmt(detail.total)}</span></div>
              <p className="text-xs text-muted-foreground">{t("soldBy")}: {detail.sold_by_name} · {detail.payment_method}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirm} onOpenChange={() => setConfirm(null)} destructive
        title="Void this sale?" description={`Voiding ${confirm?.invoice_no} will restore the sold items back to stock.`}
        confirmLabel={t("voidSale")} onConfirm={() => doVoid(confirm)} />
    </div>
  );
}
